import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { from, Observable, Subject } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';

import { DashpivotEvent, EventNotifierService, EventTypes, List } from '@site-mate/dashpivot-shared-library';

import { ListsService } from 'app/lists/lists.service';
import { SegmentService } from 'app/segment/segment.service';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';

@Component({ template: '<div></div>' })
export abstract class AbstractListPage implements OnDestroy {
  public readonly parentType: string = 'none';
  public readonly fullDisplayMode = 6;
  public readonly canDeployLists: boolean = false;
  protected readonly destroy$ = new Subject<void>();
  protected parentId: string;
  lists: List[] = [];
  readonly state = {
    isLoading: false,
    selectedListIds: [] as string[],
  };

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly confirmService: ConfirmService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly listsService: ListsService,
    protected readonly router: Router,
    protected readonly segmentService: SegmentService,
    protected readonly toastrService: ToastrService,
  ) {}

  abstract getLists(parentId: string): Observable<List[]>;
  abstract deleteLists(parentId: string, listIds: string[]): Observable<void[]>;

  onSelectList(listId: string) {
    return this.router.navigate(['/', 'lists', this.parentType, this.parentId, listId]);
  }

  onChangeSelectedState(listId: string, selected: boolean) {
    const updatedListIds = this.state.selectedListIds.filter((item) => item !== listId);
    if (selected) {
      updatedListIds.push(listId);
    }
    this.state.selectedListIds = updatedListIds;
  }

  onListsDelete(listIds: string[]) {
    from(this.confirmService.confirmDelete('deleteList'))
      .pipe(
        switchMap(() => {
          this.state.isLoading = true;
          return this.deleteLists(this.parentId, listIds);
        }),
        finalize(() => {
          this.state.selectedListIds = [];
          this.changeDetectorRef.markForCheck();
        }),
      )
      .subscribe(
        () => {
          this.toastrService.successByKey('listRemoved');
          void EventNotifierService.notify(
            new DashpivotEvent(EventTypes.ListActionApplied, { Context: 'Deleted' }),
            this.segmentService,
          );
          this.loadLists();
        },
        () => {
          // No-op
        },
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected loadLists() {
    this.state.isLoading = true;
    this.getLists(this.parentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.state.isLoading = false;
        }),
      )
      .subscribe(
        (lists) => {
          const filteredLists = lists.map((list) =>
            ListsHandler.service.filterDeployedListItems(list, this.parentId),
          );
          this.lists = filteredLists;
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
  }
}
